import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const reasons = [
  {
    num: '01',
    title: 'Primeiro percebemos, depois propomos',
    desc: 'Antes de qualquer proposta, queremos perceber a operação, os constrangimentos e o que já foi tentado. O diagnóstico é parte do trabalho, não uma formalidade.',
  },
  {
    num: '02',
    title: 'Experiência nos setores que servimos',
    desc: 'Trabalhamos há anos em ambiente industrial, agrícola e municipal. Esse contexto acumulado significa que chegamos a um projeto com menos perguntas básicas e mais foco no que importa.',
  },
  {
    num: '03',
    title: 'Entregamos e ficamos',
    desc: 'O lançamento não é o fim do trabalho. Acompanhamos a adoção, resolvemos o que aparece depois do arranque e ajustamos com base na realidade — não no que estava previsto.',
  },
  {
    num: '04',
    title: 'Quem propõe é quem executa',
    desc: 'Não há uma equipa de vendas e outra de entrega. A pessoa que entende o projeto é a mesma que o constrói. Isso elimina ruído e aumenta a responsabilidade.',
  },
  {
    num: '05',
    title: 'Construímos os nossos próprios produtos',
    desc: 'O Vulpes, o Nexo e o Logitrax são nossos. Não somos só consultores — sabemos o que é lançar, manter e melhorar tecnologia em produção com utilizadores reais.',
  },
  {
    num: '06',
    title: 'Proximidade real, não só no discurso',
    desc: 'Estamos na Covilhã, no centro do país, próximos de muitas das empresas que servimos. Preferimos relações longas a projetos únicos — e isso reflete-se em como trabalhamos.',
  },
];

const WhyUs: React.FC = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.08 });

  return (
    <section style={s.section} ref={ref}>
      <div style={s.container}>

        {/* Header */}
        <motion.div
          style={s.header}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.65 }}
        >
          <span style={s.label}>Porquê a 2GF</span>
          <h2 style={s.title}>O que nos distingue</h2>
        </motion.div>

        {/* Grid */}
        <div style={s.grid}>
          {reasons.map((r, i) => (
            <motion.div
              key={i}
              style={s.item}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, delay: 0.1 + i * 0.07 }}
            >
              <span style={s.itemNum}>{r.num}</span>
              <h3 style={s.itemTitle}>{r.title}</h3>
              <p style={s.itemDesc}>{r.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          style={s.cta}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.65, delay: 0.5 }}
        >
          <div>
            <p style={s.ctaTitle}>Tem um projeto em mente?</p>
            <p style={s.ctaText}>Fale connosco. Sem compromissos, sem propostas genéricas.</p>
          </div>
          <button
            style={s.ctaBtn}
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Falar connosco
          </button>
        </motion.div>

      </div>
    </section>
  );
};

const s: { [key: string]: React.CSSProperties } = {
  section: {
    background: '#020F1E',
    padding: '120px 0 0',
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
  },
  header: {
    marginBottom: '4rem',
  },
  label: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.25em',
    textTransform: 'uppercase',
    color: '#00C896',
    display: 'block',
    marginBottom: '1rem',
  },
  title: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
    fontWeight: 800,
    color: '#ffffff',
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    borderTop: '1px solid rgba(255,255,255,0.07)',
    borderLeft: '1px solid rgba(255,255,255,0.07)',
  },
  item: {
    padding: '2.75rem 2.5rem',
    borderRight: '1px solid rgba(255,255,255,0.07)',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
  },
  itemNum: {
    display: 'block',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.65rem',
    fontWeight: 600,
    letterSpacing: '0.2em',
    color: '#00C896',
    marginBottom: '1.25rem',
  },
  itemTitle: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '1.05rem',
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: '0.85rem',
    letterSpacing: '-0.01em',
    lineHeight: 1.3,
  },
  itemDesc: {
    fontSize: '0.875rem',
    color: 'rgba(255,255,255,0.5)',
    lineHeight: 1.9,
    margin: 0,
  },
  cta: {
    marginTop: '4rem',
    paddingTop: '3rem',
    paddingBottom: '3rem',
    borderTop: '1px solid rgba(255,255,255,0.07)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '2rem',
  },
  ctaTitle: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: '0.3rem',
    letterSpacing: '-0.01em',
  },
  ctaText: {
    fontSize: '0.9rem',
    color: 'rgba(255,255,255,0.45)',
    margin: 0,
  },
  ctaBtn: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.8rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'white',
    background: '#0057FF',
    padding: '0.9rem 2rem',
    border: 'none',
    cursor: 'pointer',
    flexShrink: 0,
  },
};

export default WhyUs;
